<template>
  <q-card class="widget-card">
    <div class="widget-card__header">
      <div class="flex items-center no-wrap">
        <q-icon
          name="sym_r_directions_car"
          size="23px"
          class="q-mr-xs"
          style="margin-top: -2px;"
        />
        <div class="widget-card__title">Popular Models</div>
      </div>
      <div class="widget-card__subtitle">
        The most listed models on the market.
      </div>
    </div>
    <div class="row q-col-gutter-sm q-px-md q-pb-sm">
      <div class="col-6">
        <q-select
          v-model="selectedCondition"
          :options="conditionOptions"
          color="secondary"
          dense
          @update:model-value="fetchPopularModels"
        />
      </div>
      <div class="col-6">
        <q-select
          v-model="selectedBodyType"
          :options="bodyTypeOptions"
          color="secondary"
          dense
          @update:model-value="fetchPopularModels"
        />
      </div>
    </div>
    <div class="widget-card__content">
      <div v-if="loading" class="q-py-md">
        <q-skeleton type="rect" height="30px" class="q-mb-sm" />
        <q-skeleton type="rect" height="30px" class="q-mb-sm" />
        <q-skeleton type="rect" height="30px" class="q-mb-sm" />
        <q-skeleton type="rect" height="30px" class="q-mb-sm" />
        <q-skeleton type="rect" height="30px" />
      </div>
      <div v-else>
        <div
          v-for="(model, index) in topModels"
          :key="index"
          class="make-item cursor-pointer"
          @click="searchByModel(model.year, model.make_id, model.model_id)"
        >
          <div class="make-item__rank">{{ index + 1 }}</div>
          <div class="make-item__content">
            <div class="make-item__name ellipsis">
              {{ model.year }} {{ model.make }} {{ model.model }}
            </div>
            <div class="make-item__price">{{ model.priceRange }}</div>
            <div class="make-item__bar-container">
              <div
                class="make-item__bar"
                :style="{
                  width: model.percentage + '%',
                  background: getBarGradient(index)
                }"
              ></div>
            </div>
          </div>
          <div class="make-item__count">{{ model.count }}</div>
          <q-icon
            name="sym_r_chevron_right"
            size="21px"
            color="text-light"
            style="margin-left: -6px"
          />
        </div>
      </div>
    </div>
  </q-card>
</template>

<script>
import axios from "axios";
import mixins from "@/mixins";

export default {
  name: "PopularModelsWidget",
  mixins: [mixins],
  data() {
    return {
      loading: true,
      topModels: [],
      selectedCondition: { label: "All Conditions", value: null },
      selectedBodyType: { label: "All Body Types", value: null },
      conditionOptions: [
        { label: "All Conditions", value: null },
        { label: "New Vehicles", value: "new" },
        { label: "Used Vehicles", value: "used" }
      ],
      bodyTypeOptions: [
        { label: "All Body Types", value: null },
        { label: "Convertible", value: 8 },
        { label: "Coupe", value: 5 },
        { label: "Hatchback", value: 6 },
        { label: "Sedan", value: 1 },
        { label: "SUV", value: 3 },
        { label: "Truck", value: 2 },
        { label: "Van", value: 4 },
        { label: "Wagon", value: 7 }
      ]
    };
  },
  mounted() {
    this.fetchPopularModels();
  },
  methods: {
    async fetchPopularModels() {
      try {
        this.loading = true;
        const params = {};

        if (this.selectedCondition?.value) {
          params.condition = this.selectedCondition.value;
        }

        if (this.selectedBodyType?.value) {
          params.body_type_id = this.selectedBodyType.value;
        }

        const response = await axios.get(
          "https://api-v2.jumpfeed.com/network/publisher/products/vehicles/most-listed-market-models",
          { params }
        );

        const models = response.data.data || [];
        const maxCount = Math.max(
          ...models.map(m => parseInt(m.total_vehicles)),
          1
        );

        this.topModels = models.slice(0, 5).map(model => ({
          year: model.year,
          make: model.make,
          model: model.model,
          make_id: model.make_id,
          model_id: model.model_id,
          count: this.formatNumber(parseInt(model.total_vehicles), true, 1),
          percentage: (parseInt(model.total_vehicles) / maxCount) * 100,
          priceRange: `$${this.formatNumber(
            parseFloat(model.min_avg_price),
            true,
            0
          )} - $${this.formatNumber(parseFloat(model.max_avg_price), true, 0)}`
        }));
      } catch (error) {
        console.error("Failed to fetch popular models:", error);
      } finally {
        this.loading = false;
      }
    },
    getBarGradient(index) {
      const gradients = [
        "linear-gradient(90deg, #E7171B 0%, #F2474B 100%)",
        "linear-gradient(90deg, #F2474B 0%, #F2656B 100%)",
        "linear-gradient(90deg, #F2656B 0%, #F2838B 100%)",
        "linear-gradient(90deg, #F2838B 0%, #F2A1AB 100%)",
        "linear-gradient(90deg, #F2A1AB 0%, #F2BFCB 100%)"
      ];
      return gradients[index] || gradients[4];
    },
    searchByModel(year, makeId, modelId) {
      this.$store.commit("search/SET_SEARCH_FILTERS", [
        { year: { gte: year, lte: year } },
        { make_id: { in: [makeId] } },
        { model_id: { in: [modelId] } }
      ]);
      this.$router.push({ name: "search" });
    }
  }
};
</script>

<style scoped lang="scss">
.make-item {
  display: flex;
  align-items: center;
  gap: $space-sm;
  padding: $space-sm 0;
  background: color(background, lighter);
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: color(background, very-light);
    transform: translateX(2px);
    padding-left: 8px;

    .make-item__bar-container {
      background: color(background, light);
    }
  }

  &__rank {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: color(background, very-very-light);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.93rem;
    font-weight: 700;
    flex-shrink: 0;
    margin-bottom: -2px;
  }

  &__content {
    flex: 1;
    min-width: 0;
    margin-left: 4px;
    margin-top: -3px;
    align-items: center;
  }

  &__name {
    font-size: 0.9rem;
    font-weight: 600;
    color: color(text);
  }

  &__price {
    font-size: 0.74rem;
    font-weight: 500;
    color: color(text, light);
    margin-bottom: 4px;
    margin-top: -4px;
  }

  &__bar-container {
    height: 7px;
    background: color(background, very-light);
    border-radius: 3px;
    overflow: hidden;
    margin-right: 8px;
    transition: all 0.2s ease;
  }

  &__bar {
    height: 100%;
    transition: width 0.6s ease;
    border-radius: 4px;
  }

  &__count {
    font-size: 0.95rem;
    font-weight: 700;
    color: color(text);
  }
}
</style>
